import toast from 'react-hot-toast';

export interface ToastOptions {
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  icon?: string;
  style?: React.CSSProperties;
  className?: string;
}

export class ToastService {
  static showError(message: string, options?: ToastOptions) {
    return toast.error(message, {
      duration: 5000,
      position: 'top-right',
      style: {
        background: 'var(--toast-error-bg)',
        color: 'var(--toast-error-text)',
        border: '1px solid var(--toast-error-border)',
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      },
      ...options,
    });
  }

  static showSuccess(message: string, options?: ToastOptions) {
    return toast.success(message, {
      duration: 3000,
      position: 'top-right',
      style: {
        background: 'var(--toast-success-bg)',
        color: 'var(--toast-success-text)',
        border: '1px solid var(--toast-success-border)',
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      },
      ...options,
    });
  }

  static showWarning(message: string, options?: ToastOptions) {
    return toast(message, {
      duration: 4000,
      position: 'top-right',
      icon: '⚠️',
      style: {
        background: 'var(--toast-warning-bg)',
        color: 'var(--toast-warning-text)',
        border: '1px solid var(--toast-warning-border)',
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      },
      ...options,
    });
  }

  static showInfo(message: string, options?: ToastOptions) {
    return toast(message, {
      duration: 3000,
      position: 'top-right',
      icon: 'ℹ️',
      style: {
        background: 'var(--toast-info-bg)',
        color: 'var(--toast-info-text)',
        border: '1px solid var(--toast-info-border)',
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      },
      ...options,
    });
  }

  static dismiss(toastId?: string) {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  }

  static dismissAll() {
    toast.dismiss();
  }
}

// Convenience functions for common error scenarios
export const showApiError = (error: any) => {
  let message = 'An unexpected error occurred';
  
  if (error?.response?.data?.message) {
    message = error.response.data.message;
  } else if (error?.message) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  return ToastService.showError(message);
};

export const showNetworkError = () => {
  return ToastService.showError('Network error. Please check your connection and try again.');
};

export const showValidationError = (message: string) => {
  return ToastService.showError(`Validation error: ${message}`);
};

export const showAuthError = (message: string) => {
  return ToastService.showError(`Authentication error: ${message}`);
};

export default ToastService;
